import React, { useImperativeHandle, useRef, useState } from 'react';
import { Button, Form, Input, Spin, Switch, message } from 'antd';
import { CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { renderIntlLabel } from '@/utils';
import { getSmsTemplate } from '@/api/sms';
import { fetchCustomerInfoByWyuid, fetchGiveAwayCoupon } from '@/api/guest';
import CustomDrawer, { IdrawerRef } from '../CustomDrawer';
import SmsOpenDrawer from './SmsOpenDrawer';
import styles from './index.module.scss';

export interface PresentConfirmImperativeHandle {
  openDrawer: (data?: { useInfo: object; record: object; onOk: () => void }) => void;
}

const PresentConfirmDrawer = React.forwardRef<PresentConfirmImperativeHandle, any>((props, ref) => {
  const [useInfo, setUseInfo] = useState<any>();
  const [record, setRecord] = useState<any>();

  const [smsTemplate, setSmsTemplate] = useState<any>({});
  const [configLoading, setConfigLoading] = useState(false);
  // 短信switch
  const [checked, setChecked] = useState(false);
  const [giveLoading, setGiveLoading] = useState(false);

  const [form] = Form.useForm();

  const drawerRef = useRef<IdrawerRef>();
  const paramsRef = useRef({});
  const smsRef = useRef<any>();

  const getSmsConfig = async ({ useInfo: info, record: couponInfo }) => {
    const { wyuid } = info;
    const {
      couponCode,
      couponName,
      couponFaceValueText,
      shopGiveCouponRangeConfigId,
      shopGiveCouponRuleId,
    } = couponInfo;
    try {
      setConfigLoading(true);
      const res = await getSmsTemplate({
        wyuid,
        couponCode,
        couponName,
        couponDescribe: couponFaceValueText,
        shopGiveCouponRangeConfigId,
        shopGiveCouponRuleId,
        quantity: 1,
        openSendMessage: 0,
        reason: '吴玉林',
      });
      if (res.code === 0) {
        setSmsTemplate(res.data);
        // 开启赠券短信服务
        setChecked(res.data.openStatus === 1);
      } else {
        // message.error(res.msg);
      }
      setConfigLoading(false);
    } catch (error) {
      setConfigLoading(false);
    }
  };

  const handleOpenDrawer = async (data) => {
    drawerRef.current.openDrawer();
    form.resetFields();

    const { useInfo: info, record: rd, onOk } = data;
    setUseInfo(info);
    setRecord(rd);
    paramsRef.current = onOk;

    // 获取短信模版
    getSmsConfig({ useInfo: info, record: rd });
  };

  const handleSwitch = (che) => {
    // 开启
    if (smsTemplate.openStatus === 1) {
      setChecked(che);
    } else {
      message.error(renderIntlLabel('guest_sms_msg_service_is_not_enabled'));
    }
  };

  const handleGiveImmediately = async () => {
    const values = await form.validateFields();
    if (checked && !smsTemplate?.chinaMobile) {
      message.error(renderIntlLabel('app_only_support_china_mobile'));
      return;
    }
    const { wyuid } = useInfo;
    const {
      couponCode,
      couponName,
      couponFaceValueText,
      shopGiveCouponRangeConfigId,
      shopGiveCouponRuleId,
    } = record;
    const params = {
      wyuid,
      couponCode,
      couponDescribe: couponFaceValueText,
      couponName,
      shopGiveCouponRangeConfigId,
      shopGiveCouponRuleId,
      quantity: 1,
      reason: values.reason,
      openSendMessage: checked ? 1 : 0,
    };
    try {
      setGiveLoading(true);
      const res = await fetchGiveAwayCoupon(params);
      if (res.code === 0) {
        if (res.data.giveCouponSuccess) {
          message.success(renderIntlLabel('app_coupon_give_successfully'));
        }
        if (res.data.sendMessageSuccess) {
          message.success(renderIntlLabel('app_sms_send_successfully'));
        }
        if (typeof paramsRef.current === 'function') {
          paramsRef.current();
        }
        drawerRef.current.closeDrawer();
      } else {
        // message.error(res.msg);
      }
      setGiveLoading(false);
    } catch (error) {
      setGiveLoading(false);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      openDrawer: handleOpenDrawer,
    }),
    [],
  );

  return (
    <>
      <CustomDrawer
        className={styles.confirmContainer}
        width="50%"
        title={renderIntlLabel('guest_title_confirm_of_give')}
        ref={drawerRef}
        footer={
          <>
            <Button type="primary" loading={giveLoading} onClick={handleGiveImmediately}>
              {renderIntlLabel('guest_title_give_immediately')}
            </Button>
            <Button onClick={() => drawerRef.current.closeDrawer()}>
              {renderIntlLabel('app_btn_cancel')}
            </Button>
          </>
        }
      >
        {/* 短信开通组件 */}
        <SmsOpenDrawer ref={smsRef} />

        <div className="groupLabel" style={{ marginBottom: '16px' }}>
          {renderIntlLabel('guest_title_give_coupon_info')}
        </div>
        <div style={{ marginBottom: '8px' }}>
          {renderIntlLabel('guest_sms_msg_cannot_withdraw_after_give')}
        </div>
        <Form form={form} labelCol={{ span: 3 }} labelAlign="left">
          <Form.Item label={renderIntlLabel('order_roomorder_name')}>{useInfo?.name}</Form.Item>
          <Form.Item label={renderIntlLabel('order_roomorder_cert_no')}>
            {useInfo?.certNo}
          </Form.Item>
          <Form.Item label={renderIntlLabel('channel_crs_label_coupon')}>
            {record?.couponName}
          </Form.Item>
          <Form.Item label={renderIntlLabel('order_goods_quantity')}>1</Form.Item>
          <Form.Item
            label={renderIntlLabel('guest_coupon_label_giveReason')}
            name="reason"
            rules={[
              {
                required: true,
                message: renderIntlLabel('guest_coupon_placeholder_please_input_give_reason'),
              },
            ]}
          >
            <Input.TextArea
              maxLength={100}
              rows={5}
              placeholder={renderIntlLabel('guest_coupon_placeholder_inner_remark_required')}
            />
          </Form.Item>
        </Form>

        <div className="groupLabel" style={{ marginTop: '32px', marginBottom: '16px' }}>
          {renderIntlLabel('guest_title_sms_remind')}
        </div>
        <Spin spinning={configLoading}>
          {smsTemplate?.openStatus === 1 ? (
            <div className={styles.opened}>
              <div>
                <CheckCircleOutlined className={styles.icon} />
                {renderIntlLabel('guest_sms_msg_currentShop_service_is_enabled')}
              </div>
              <Button type="primary" onClick={() => smsRef.current?.openDrawer()}>
                {renderIntlLabel('guest_title_sms_send_service')}
              </Button>
            </div>
          ) : (
            <div className={styles.notOpened}>
              <div>
                <InfoCircleOutlined className={styles.icon} />
                {renderIntlLabel('guest_sms_msg_currentShop_service_is_not_enabled')}
                <span
                  className={styles.refreshBtn}
                  onClick={() => getSmsConfig({ useInfo, record })}
                >
                  {renderIntlLabel('guest_title_refresh_status')}
                </span>
              </div>
              <Button type="primary" onClick={() => smsRef.current?.openDrawer()}>
                {renderIntlLabel('guest_title_sms_send_service')}
              </Button>
            </div>
          )}
          <Form labelCol={{ span: 3 }} labelAlign="left">
            <Form.Item
              labelCol={{ span: 5 }}
              label={renderIntlLabel('guest_title_send_sms_at_the_same_time')}
            >
              <Switch checked={checked} onChange={handleSwitch} />
            </Form.Item>
            <Form.Item label={renderIntlLabel('login_sms_ph')}>{smsTemplate?.phone}</Form.Item>
            <Form.Item label={renderIntlLabel('guest_title_sms_content')}>
              <Input.TextArea disabled value={smsTemplate?.content} />
            </Form.Item>
            <Form.Item label={renderIntlLabel('app_sms_char_counts')}>
              {smsTemplate?.contentLength}
            </Form.Item>
          </Form>
        </Spin>
      </CustomDrawer>
    </>
  );
});

export default PresentConfirmDrawer;
